import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Label } from "../ui/label";
import { Switch } from "../ui/switch";
import { Badge } from "../ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { PERMISSION_LABELS } from "./PortalTemplatesTab";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function UserAccessControlDialog({ open, onOpenChange, portalUser, onSaved }) {
  const [loading, setLoading] = useState(false);
  const [builtIn, setBuiltIn] = useState([]);
  const [custom, setCustom] = useState([]);
  const [catalog, setCatalog] = useState({ employee_portal: [], client_portal: [] });
  const [allOtherUsers, setAllOtherUsers] = useState([]);
  const [templateKey, setTemplateKey] = useState("");
  const [templateSource, setTemplateSource] = useState("built_in");
  const [permissions, setPermissions] = useState({});
  const [allowsDelegation, setAllowsDelegation] = useState(false);
  const [delegatedTargets, setDelegatedTargets] = useState([]);
  const [delegatedKeys, setDelegatedKeys] = useState([]);

  const category = portalUser?.account_type || "employee_portal";
  const permissionKeys = catalog[category] || [];

  const reset = useCallback(() => {
    setTemplateKey("");
    setTemplateSource("built_in");
    setPermissions({});
    setAllowsDelegation(false);
    setDelegatedTargets([]);
    setDelegatedKeys([]);
  }, []);

  const loadAll = useCallback(async () => {
    if (!portalUser?.id) return;
    setLoading(true);
    try {
      const [tplRes, ctrlRes, usersRes] = await Promise.all([
        axios.get(`${API}/portal-templates`),
        axios.get(`${API}/portal/users/${portalUser.id}/access-control`),
        axios.get(`${API}/portal/users`),
      ]);
      const bi = tplRes.data?.built_in || [];
      const cu = tplRes.data?.custom || [];
      setBuiltIn(bi);
      setCustom(cu);
      setCatalog(tplRes.data?.permission_catalog || { employee_portal: [], client_portal: [] });
      const ctrl = ctrlRes.data;
      setTemplateKey(ctrl?.template_key || "");
      setTemplateSource(ctrl?.template_source || "built_in");
      setPermissions(ctrl?.permissions || {});
      setDelegatedTargets(ctrl?.delegated_target_user_ids || []);
      setDelegatedKeys(ctrl?.delegated_permission_keys || []);
      setWidgetEnabled(!!ctrl?.delegation_widget_enabled);
      // Determine delegation flag from template
      const matchedBI = bi.find((p) => p.key === ctrl?.template_key);
      const matchedCu = cu.find((t) => t.id === ctrl?.template_key);
      setAllowsDelegation(
        (ctrl?.template_source === "custom" ? !!matchedCu?.allows_delegation : !!matchedBI?.allows_delegation)
      );
      setAllOtherUsers((usersRes.data || []).filter((u) => u.id !== portalUser.id));
    } catch (e) {
      toast.error("Failed to load access control");
    } finally {
      setLoading(false);
    }
  }, [portalUser?.id]);

  useEffect(() => {
    if (open) {
      reset();
      loadAll();
    }
  }, [open, loadAll, reset]);

  const onTemplateChange = (value) => {
    if (!value) return;
    const [source, key] = value.split("::");
    setTemplateKey(key);
    setTemplateSource(source);
    let tpl = null;
    if (source === "built_in") tpl = builtIn.find((p) => p.key === key);
    else tpl = custom.find((t) => t.id === key);
    if (tpl) {
      setPermissions({ ...(tpl.permissions || {}) });
      setAllowsDelegation(!!tpl.allows_delegation);
      if (!tpl.allows_delegation) {
        setDelegatedTargets([]);
        setDelegatedKeys([]);
        setWidgetEnabled(false);
      }
    }
  };

  const togglePerm = (key) => {
    setPermissions((p) => ({ ...p, [key]: !p[key] }));
  };

  const toggleDelegateTarget = (uid) => {
    setDelegatedTargets((t) => (t.includes(uid) ? t.filter((x) => x !== uid) : [...t, uid]));
  };

  const toggleDelegateKey = (k) => {
    setDelegatedKeys((d) => (d.includes(k) ? d.filter((x) => x !== k) : [...d, k]));
  };

  const save = async () => {
    try {
      await axios.put(`${API}/portal/users/${portalUser.id}/access-control`, {
        template_key: templateKey || null,
        template_source: templateSource || "built_in",
        permissions,
        delegated_target_user_ids: delegatedTargets,
        delegated_permission_keys: delegatedKeys,
        delegation_widget_enabled: widgetEnabled,
      });
      toast.success("Access control updated");
      onOpenChange(false);
      onSaved && onSaved();
    } catch (e) {
      const detail = e?.response?.data?.detail || "Failed to save";
      toast.error(detail);
    }
  };

  const visibleBuiltIn = builtIn.filter((p) => p.category === category);
  const visibleCustom = custom.filter((t) => t.category === category);
  const currentTemplateValue = templateKey ? `${templateSource}::${templateKey}` : "";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl max-h-[88vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Manage Portal Access</DialogTitle>
          <DialogDescription asChild>
            <div className="text-sm text-muted-foreground">
              <span className="font-medium">{portalUser?.name}</span> · {portalUser?.email} ·{" "}
              <Badge variant="outline" className="text-[10px]">
                {category === "client_portal" ? "Client Portal" : "Employee Portal"}
              </Badge>
            </div>
          </DialogDescription>
        </DialogHeader>
        {loading ? (
          <p className="text-sm text-muted-foreground py-4">Loading…</p>
        ) : (
          <div className="space-y-5 py-2">
            <div>
              <Label className="text-sm font-semibold">Template</Label>
              <Select value={currentTemplateValue} onValueChange={onTemplateChange}>
                <SelectTrigger data-testid="user-template-select">
                  <SelectValue placeholder="Choose a persona template…" />
                </SelectTrigger>
                <SelectContent>
                  {visibleBuiltIn.map((p) => (
                    <SelectItem key={p.key} value={`built_in::${p.key}`}>
                      {p.name}
                    </SelectItem>
                  ))}
                  {visibleCustom.length > 0 && (
                    <>
                      {visibleCustom.map((t) => (
                        <SelectItem key={t.id} value={`custom::${t.id}`}>
                          ✦ {t.name} (Custom)
                        </SelectItem>
                      ))}
                    </>
                  )}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground mt-1">
                Choosing a template will fill the permissions below — feel free to tweak after.
              </p>
            </div>

            <div>
              <Label className="text-sm font-semibold">Permissions</Label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-2">
                {permissionKeys.map((k) => (
                  <div
                    key={k}
                    className="flex items-center justify-between gap-2 rounded-md border px-3 py-2 bg-card"
                  >
                    <Label htmlFor={`user-perm-${k}`} className="text-xs cursor-pointer">
                      {PERMISSION_LABELS[k] || k}
                    </Label>
                    <Switch
                      id={`user-perm-${k}`}
                      checked={!!permissions[k]}
                      onCheckedChange={() => togglePerm(k)}
                      data-testid={`user-perm-toggle-${k}`}
                    />
                  </div>
                ))}
              </div>
            </div>

            {allowsDelegation && (
              <div className="space-y-3 rounded-xl border-2 border-amber-500/30 p-3 bg-amber-50/40 dark:bg-amber-950/20">
                <div>
                  <Label className="text-sm font-semibold flex items-center gap-2">
                    🛡️ Delegated Portal-User Management
                  </Label>
                  <p className="text-xs text-muted-foreground">
                    This template allows delegation. Choose which OTHER portal users this person can manage, and which permission keys they're allowed to toggle. They can never change Owner accounts or their own account.
                  </p>
                </div>
                <div className="flex items-center justify-between gap-2 rounded-md border bg-card px-3 py-2">
                  <div>
                    <Label className="text-xs font-semibold">Show Delegation Widget on Dashboard</Label>
                    <p className="text-[11px] text-muted-foreground">
                      Adds a "Portal Users I Manage" card to this user's dashboard. You can toggle this anytime.
                    </p>
                  </div>
                  <Switch
                    checked={widgetEnabled}
                    onCheckedChange={setWidgetEnabled}
                    data-testid="delegation-widget-switch"
                  />
                </div>
                <div>
                  <Label className="text-xs font-semibold">Can manage these portal users</Label>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-1 mt-1 max-h-40 overflow-y-auto">
                    {allOtherUsers.length === 0 && (
                      <p className="text-xs text-muted-foreground italic">No other portal users available.</p>
                    )}
                    {allOtherUsers.map((u) => (
                      <label
                        key={u.id}
                        className="flex items-center gap-2 text-xs rounded px-2 py-1 hover:bg-muted/50 cursor-pointer"
                      >
                        <input
                          type="checkbox"
                          checked={delegatedTargets.includes(u.id)}
                          onChange={() => toggleDelegateTarget(u.id)}
                          data-testid={`delegate-target-${u.id}`}
                        />
                        <span className="flex-1 truncate">{u.name}</span>
                        <Badge variant="outline" className="text-[9px]">
                          {u.account_type === "client_portal" ? "C" : "E"}
                        </Badge>
                      </label>
                    ))}
                  </div>
                </div>
                <div>
                  <Label className="text-xs font-semibold">Can toggle these permission keys</Label>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-1 mt-1 max-h-40 overflow-y-auto">
                    {permissionKeys.map((k) => (
                      <label
                        key={k}
                        className="flex items-center gap-2 text-xs rounded px-2 py-1 hover:bg-muted/50 cursor-pointer"
                      >
                        <input
                          type="checkbox"
                          checked={delegatedKeys.includes(k)}
                          onChange={() => toggleDelegateKey(k)}
                          data-testid={`delegate-key-${k}`}
                        />
                        <span>{PERMISSION_LABELS[k] || k}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={save} disabled={loading} data-testid="save-access-control-btn">
            Save Access
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
